import axios from 'axios';
import moment from 'moment';
import { Buffer } from 'buffer';
import { get as lodashGet } from 'lodash';

import { getCameraStatus } from '../../helpers/api';
import store from '../../store';
import { updatePath } from './actions';
import { buildURL } from '../../helpers/urls';
import env from '../../config/env';
import {
  setRowDict,
  getRowDict,
  _setRowDictForTesting,
  getRowOrder,
  isRowInRegion,
  interpolate2,
  calculatePanTilt,
} from './acsSeatMath';

const adminUser = env.BRIZI_ADMIN_USERNAME;
const adminPass = env.BRIZI_ADMIN_PASSWORD;

/** Session cookie only — no Basic auth (prod CORS blocks it on /mapping/download). */
const camFileRequestConfig = (extra = {}) => ({
  withCredentials: true,
  ...extra,
});

let camMap;

// Helper functions for testing
const _setCamMapForTesting = (map) => {
  camMap = map;
};
const _getCamMapForTesting = () => camMap;

const isOddEvenModeEnabled = () => env.ODD_EVEN_MODE === true;
const decodeOddEvenSeat = (seat) => (isOddEvenModeEnabled() && seat > 100 ? seat - 100 : seat);

/**
 * Public cam-file API for Mapping + Arrange.
 * Prefer these over inventing new config fetch/upload paths.
 */

/** Live camera indexes (cam1, cam2, …) from camera status. */
export const listMappingCams = async () => {
  const data = await getCameraStatus();
  return Object.keys(data || {})
    .map((k) => String(k).trim().toLowerCase())
    .filter((k) => /^cam\d+$/.test(k));
};

/** Fetch a cam mapping JSON without touching the in-memory camMap singleton. */
export const fetchCamMapJson = async (camIdx) => {
  // Session-auth route (adminAuth) — not /config Basic auth.
  const mappingFileUrl = buildURL('/mapping/download', `${camIdx}.json`);
  const response = await axios.get(mappingFileUrl, camFileRequestConfig({
    headers: {
      Accept: 'application/json',
      'AMP-User-Id': lodashGet(store.getState(), 'authentication.userData._id', 'dashboard'),
    },
    params: { _: Date.now() },
  }));
  return response.data;
};

const getCamMapFile = async (camIdx) => {
  try {
    camMap = await fetchCamMapJson(camIdx);
  } catch (error) {
    console.error('Error while getting cam map file: ', error);
  }
};

export const getRowDictFile = async () => {
  const rowDictFileUrl = buildURL('/mapping/download', 'row_dict.txt');
  try {
    const response = await axios.get(rowDictFileUrl, camFileRequestConfig({
      headers: {
        Accept: 'application/json',
        'AMP-User-Id': lodashGet(store.getState(), 'authentication.userData._id', 'dashboard'),
      },
    }));
    setRowDict(response.data);
    return response.data;
  } catch (error) {
    console.error(`Could not get --> ${rowDictFileUrl} - ${error}`);
    return undefined;
  }
};

const loadMapConfig = async (camIdx) => {
  try {
    await getRowDictFile();
    await getCamMapFile(camIdx);
    store.dispatch(updatePath('loadingStatus', `${camIdx}FileLoaded ${new Date().toISOString()}`));
    console.log('\nSuccessfully parsed mapping files\n');
    return true;
  } catch (error) {
    console.error(`Could not retrieve mapping file or row_dict: ${error}`);
    return false;
  }
};

// return reference point from the camera mapping json object
const getReference = () => {
  try {
    // Check if jsonObj is defined and has the "ref_point" property
    if (camMap?.ref_point
      && !Number.isNaN(camMap.ref_point.pan)
      && !Number.isNaN(camMap.ref_point.tilt)
      && !Number.isNaN(camMap.ref_point.zoom)) {
      return camMap.ref_point;
    }
    // Log a warning if ref_point is not found
    console.warn('ref_point not found in the JSON object.');
    return null;
  } catch (error) {
    console.error('Error getting reference point:', error);
    return null;
  }
};

const isValidJSONObject = (obj) => {
  try {
    // Convert the object to a JSON string
    const jsonString = JSON.stringify(obj);
    // Parse the JSON string back to an object
    JSON.parse(jsonString);
    return true; // Both operations succeeded, object is valid JSON data
  } catch (error) {
    console.error(`Error validating JSON object: ${error}`);
    return false; // An error occurred, object is not valid JSON data
  }
};

/** Upload a cam mapping JSON for an arbitrary camIdx (stamps dashboard metadata on the payload). */
export const uploadCamMapJson = async (camIdx, data) => {
  const appSlug = process.env.BRIZI_APP_SLUG;
  const eventUrl = process.env.BRIZI_API_AUTHORITY.replace('api', appSlug);

  const payload = {
    ...data,
    camera: Number.parseInt(String(camIdx).replace('cam', '').replace('.json', ''), 10),
    event_url: eventUrl,
    section_count: data.sections.length,
    operators: [{
      id: 'Mapping Dashboard',
      timestamp: moment().format('YYYY-MM-DD HH:mm:ss Z'),
    }],
    ptu: {
      model: 'onvif',
    },
  };

  const formData = new FormData();
  formData.append('event_url', eventUrl);
  formData.append('camidx', camIdx);
  formData.append(
    'file',
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    `${camIdx}.json`,
  );

  const response = await axios.post(
    buildURL(`/events/${appSlug}/configs/JSON`),
    formData,
    {
      headers: {
        Authorization: `Basic ${Buffer.from(`${adminUser}:${adminPass}`).toString('base64')}`,
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
        'AMP-User-Id': lodashGet(store.getState(), 'authentication.userData._id', 'dashboard'),
      },
    },
  );
  return response.status === 200;
};

const uploadMappingFile = async () => {
  try {
    if (!isValidJSONObject(camMap)) {
      console.error('Invalid JSON object');
      return false;
    }
    const { mapping } = store.getState();
    const ok = await uploadCamMapJson(mapping.connectedCamera, camMap);
    if (ok) {
      console.log('uploadMappingFile successful, response.status:', 200);
    } else {
      console.log('uploadMappingFile failed, response.status: not 200');
    }
    return ok;
  } catch (error) {
    console.error('Error uploading:', error);
    return false;
  }
};

const updateRefPoint = (pan, tilt, zoom) => {
  try {
    const numericPan = Number(pan);
    const numericTilt = Number(tilt);
    const numericZoom = Number(zoom);

    if (Number.isNaN(numericPan) || Number.isNaN(numericTilt) || Number.isNaN(numericZoom)) {
      console.error('Invalid numeric values for pan or tilt or zoom');
      return false;
    }

    if (!camMap) {
      console.warn('camMap is invalid');
      return false;
    }

    camMap.ref_point = {
      pan: numericPan,
      tilt: numericTilt,
      zoom: numericZoom,
    };

    return uploadMappingFile();
  } catch (error) {
    console.error('Error updating ref point:', error);
    return false;
  }
};

// get reference image from the server
const getRefImage = async () => {
  try {
    const { mapping } = store.getState();
    const camIdx = mapping.connectedCamera;
    console.log('camIdx', camIdx);

    // /config shots are Basic-auth guarded (session cookie alone is not enough).
    const refImageUrl = buildURL(`/config/${process.env.BRIZI_APP_SLUG}/shots`, `${camIdx}.jpg`);
    const basic = Buffer.from(`${adminUser || ''}:${adminPass || ''}`).toString('base64');

    const response = await axios.get(refImageUrl, camFileRequestConfig({
      responseType: 'arraybuffer',
      headers: {
        Authorization: `Basic ${basic}`,
        Accept: 'application/json',
        'AMP-User-Id': lodashGet(store.getState(), 'authentication.userData._id', 'dashboard'),
      },
    }));
    const imageData = Buffer.from(response.data);

    // Print the size of the image data
    console.log('getRefImage successful:', (imageData.length / 1024).toFixed(2), 'KB');
    return imageData; // Return the image data directly
  } catch (error) {
    console.error('Error getting ref image:', error);
    return false;
  }
};

const updateRefImage = async (file) => {
  try {
    const { mapping } = store.getState();
    const camIdx = mapping.connectedCamera;
    console.log('camIdx', camIdx);

    const eventUrl = process.env.BRIZI_APP_SLUG;

    let imageData;
    if (file) {
      imageData = file;
    } else {
      const base64String = document.getElementById('streamPreview')?.src.replace('data:image/jpeg;base64,', '');

      if (!base64String) {
        console.error('Could not get base64 string from image');
        return false;
      }

      // Validate base64 string if needed

      const arrayBuffer = Uint8Array.from(atob(base64String), (c) => c.charCodeAt(0)).buffer;
      imageData = new Blob([arrayBuffer], { type: 'image/jpeg' });
    }

    const formData = new FormData();
    formData.append('event_url', eventUrl);
    formData.append('camidx', camIdx);
    formData.append('file', imageData, `${camIdx}.jpg`);
    const base64Auth = Buffer.from(`${adminUser}:${adminPass}`).toString('base64');
    const authHeader = `Basic ${base64Auth}`;

    const response = await axios.post(buildURL(`/events/${process.env.BRIZI_APP_SLUG}/configs/JPG`), formData, {
      headers: {
        Authorization: authHeader,
        'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
        'AMP-User-Id': lodashGet(store.getState(), 'authentication.userData._id', 'dashboard'),
      },
    });

    if (response.status === 200) {
      console.log('updateRefImage successful, response.status:', response.status);
      return true;
    }
    console.log('updateRefImage failed, response.status:', response.status);
    return false;
  } catch (error) {
    console.error('Error uploading ref image:', error);
    return false;
  }
};

const isCornerValid = (corner) => {
  try {
    if (!corner || typeof corner !== 'object') {
      return false;
    }
    return !Number.isNaN(corner.pan) && !Number.isNaN(corner.tilt)
      && !Number.isNaN(corner.row) && !Number.isNaN(corner.seat);
  } catch (error) {
    console.error('Error checking corner validity:', error);
    return false;
  }
};

// parse a camera mapping json object and return a list of formatted
// strings for dropdown options in the check tab
const populateCheckTabDropDownList = () => {
  console.log('populateCheckTabDropDownList called');
  try {
    // Check if camMap is defined and has the "sections" property
    if (!camMap?.sections) {
      console.warn('camMap is invalid');
      return [];
    }
    const parsedList = [];
    let index = 0;
    camMap?.sections.forEach((section) => {
      const sectionName = section.name;
      const type = (section.type).toUpperCase();

      section.regions.forEach((region) => {
        // Find corners based on specific positions
        const topLeftCorner = region.find((corner) => corner.position === 'top_left');
        const bottomRightCorner = region.find((corner) => corner.position === 'bottom_right');

        if (!isCornerValid(topLeftCorner) || !isCornerValid(bottomRightCorner)) {
          console.warn(`Missing corners in section ${sectionName}`);
          return;
        }

        // Extract values from specified corners
        const maxRow = topLeftCorner.row;
        const minSeat = topLeftCorner.seat;
        const minRow = bottomRightCorner.row;
        const maxSeat = bottomRightCorner.seat;

        const formattedString = `${type} ${sectionName}, R ${minRow} to ${maxRow}, S ${minSeat} to ${maxSeat}`;

        parsedList.push({
          label: formattedString,
          value: formattedString,
          index,
          sectionName,
          sectionType: type,
        });
        index += 1;
      });
    });

    return parsedList;
  } catch (error) {
    console.error('Error populating check tab dropdown list:', error);
    return [];
  }
};

// return the region object that matches the provided criteria
// (rows and seats from top_left and bottom_right corners)
const getRegionByCorners = (
  sectionName,
  rowTopLeft,
  seatTopLeft,
  rowBottomLeft,
  seatBottomRight,
) => {
  try {
  // Check if camMap is defined and has the "sections" property
    if (camMap?.sections) {
      // Convert sectionName to uppercase for comparison
      const sectionNameUpper = sectionName.toUpperCase();

      // Iterate over each section in the camMap
      // eslint-disable-next-line no-restricted-syntax
      for (const section of camMap.sections) {
        // Convert section's name to uppercase for comparison
        const sectionNameInSection = section.name.toUpperCase();

        // Check if the section's name matches the provided sectionName
        if (sectionNameInSection === sectionNameUpper) {
          // Iterate over the regions in the current section
          // eslint-disable-next-line no-restricted-syntax
          for (const region of section.regions) {
            // Find the top_left and bottom_right corners in the region
            const topLeftCorner = region.find((corner) => corner.position === 'top_left');
            const bottomRightCorner = region.find((corner) => corner.position === 'bottom_right');

            // Check if both corners are found and match the provided criteria
            /* eslint-disable max-len */
            if (isCornerValid(topLeftCorner) && isCornerValid(bottomRightCorner)
                  && topLeftCorner.row.toString().toUpperCase() === rowTopLeft.toString().toUpperCase()
                  && topLeftCorner.seat === seatTopLeft
                  && bottomRightCorner.row.toString().toUpperCase() === rowBottomLeft.toString().toUpperCase()
                  && bottomRightCorner.seat === seatBottomRight) {
              // If all conditions are met, return the region
              return region;
              /* eslint-enable max-len */
            }
          }
        }
      }
    }
    // If no matching region is found, or camMap is not in the expected format, return null
    return null;
  } catch (error) {
    console.error('Error getting region by corners:', error);
    return null;
  }
};

const getRegion = (selectedIndex, selectedName, selectedType) => {
  // Check if all arguments are defined
  if (selectedIndex === undefined || selectedName === undefined || selectedType === undefined) {
    console.error('findRegion: Invalid arguments');
    return null;
  }

  try {
    console.log(
      'findRegion called with selectedIndex, selectedName, selectedType:',
      selectedIndex,
      selectedName,
      selectedType,
    );

    let globalRegionIndex = 0;
    const upperSelectedName = selectedName.trim().toUpperCase();
    const upperSelectedType = selectedType.trim().toUpperCase();

    // Iterate over sections
    for (let sectionIndex = 0; sectionIndex < camMap.sections.length; sectionIndex++) {
      const section = camMap.sections[sectionIndex];

      // Check if the section matches the selectedName and selectedType
      if (section.name.trim().toUpperCase() === upperSelectedName
          && section.type.trim().toUpperCase() === upperSelectedType) {
        // Iterate over regions within the matching section
        for (let regionIndex = 0; regionIndex < section.regions.length; regionIndex++) {
          if (globalRegionIndex === selectedIndex) {
            // If the global index matches, return the region
            console.log(`Region found at index ${selectedIndex} in section: ${section.name}`);
            return section.regions[regionIndex];
          }
          globalRegionIndex += 1;
        }
      } else {
        // Increment globalRegionIndex by the number of regions in non-matching sections
        globalRegionIndex += section.regions.length;
      }
    }


    console.warn(`Region with index ${selectedIndex} not found in section ${selectedName}`);
    return null;
  } catch (error) {
    console.error('Error finding region:', error);
    return null;
  }
};


// this function deletes a region from the camera mapping json object
// it uploads the mapping file after the deletion to the server
// it then called loadMapConfig to reload the mapping file
// this can be made faster by not calling loadMapConfig
const deleteRegion = async (selectedIndex, selectedName, selectedType) => {
  // Check if all arguments are defined
  if (selectedIndex === undefined || selectedName === undefined || selectedType === undefined) {
    console.error('deleteRegion: Invalid arguments');
    return false;
  }

  try {
    console.warn(
      'deleteRegion called with selectedIndex, selectedName, selectedType:',
      selectedIndex,
      selectedName,
      selectedType,
    );

    let globalRegionIndex = 0;
    const upperSelectedName = selectedName.trim().toUpperCase();
    const upperSelectedType = selectedType.trim().toUpperCase();

    for (let sectionIndex = 0; sectionIndex < camMap.sections.length; sectionIndex++) {
      const section = camMap.sections[sectionIndex];
      if (section.name.trim().toUpperCase() === upperSelectedName
          && section.type.trim().toUpperCase() === upperSelectedType) {
        for (let regionIndex = 0; regionIndex < section.regions.length; regionIndex++) {
          if (globalRegionIndex === selectedIndex) {
            console.warn(`Deleting region index ${selectedIndex} from section: ${section.name}`);
            section.regions.splice(regionIndex, 1);

            // If there are no more regions in the section, delete the section as well
            if (section.regions.length === 0) {
              console.warn(`Deleting section: ${section.name}`);
              camMap.sections.splice(sectionIndex, 1);
            }

            // Upload the updated mapping file
            // eslint-disable-next-line no-await-in-loop
            await uploadMappingFile();
            const { mapping } = store.getState();
            const camIdx = mapping.connectedCamera;
            // eslint-disable-next-line no-await-in-loop
            const result = await loadMapConfig(camIdx);
            return result;
          }
          globalRegionIndex += 1;
        }
      } else {
        // Increment globalRegionIndex by the number of regions in the current section
        // since these regions are not part of the target section and should be skipped
        globalRegionIndex += section.regions.length;
      }
    }

    console.warn(`Region with index ${selectedIndex} not found in section ${selectedName}`);
    return false;
  } catch (error) {
    console.error('Error deleting region:', error);
    return false;
  }
};

const getSection = (sectionName, type) => {
  try {
    if (!sectionName || !type) {
      console.error('getSection: Invalid arguments');
      return null;
    }

    const section = camMap.sections.find((sec) => sec.name === sectionName && sec.type === type);

    return section || null;
  } catch (error) {
    console.error('Error getting section:', error);
    return null;
  }
};

// this function will return true if all corners have even seats or odd seats
// it returns false if some corners are even and others are odd
const checkCornersSeatParity = (updatedCorners) => {
  // Check if there are no corners to avoid false positives
  if (updatedCorners.length === 0) {
    return false;
  }

  // Determine the parity of the first corner's seat
  const initialParity = updatedCorners[0].seat % 2;

  // Check if all corners maintain the same parity
  return updatedCorners.every((corner) => corner.seat % 2 === initialParity);
};

// add new region to section, if section does not exist, create a new section
const addRegion = async (
  sectionName,
  sectionType,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  angle,
  zoomFactor,
  cornerChecked = false,
  mapperName = '',
) => {
  // Check that all corner arguments are defined and objects, and angle and zoom_factor are numbers
  if (!sectionName || !sectionType
    || ![topLeft, topRight, bottomLeft, bottomRight].every((corner) => isCornerValid(corner))
    || typeof angle !== 'number' || typeof zoomFactor !== 'number' || typeof cornerChecked !== 'boolean') {
    console.error('createRegion: Invalid arguments');
    return false;
  }

  try {
    /* note:
    Rove tab: always show raw position without considering ref point
    Browse tab: always show raw position without considering ref point
    preview pos: always show raw position without considering ref point
    Check tab ONLY: always show position with ref point calc
    position to sent to map file: mapFilePos = camPos - refPos
    */
    const refPoint = getReference();
    if (!refPoint) {
      console.error('Reference point not found');
      return false;
    }

    const region = getRegionByCorners(
      sectionName,
      topLeft.row,
      topLeft.seat,
      bottomRight.row,
      bottomRight.seat,
    );

    if (region) {
      console.error('Region already exists!');
      return false;
    }

    // Attempt to get the section; if not found, create a new one
    let section = getSection(sectionName, sectionType);
    if (!section) {
      console.log(`Section ${sectionName} of sectionType ${sectionType} not found. Creating a new section.`);
      section = {
        name: sectionName.trim(),
        type: sectionType,
        regions: [], // Initialize with an empty regions array
      };
      camMap.sections.push(section); // Add the new section to camMap
    }

    // check if even odd is enabled
    const evenOddFlag = isOddEvenModeEnabled();
    console.warn('evenOddFlag', evenOddFlag);
    // update corners with respect to the reference point
    const updatedCorners = [topLeft, topRight, bottomLeft, bottomRight].map((corner) => ({
      ...corner,
      pan: corner.pan - refPoint.pan,
      tilt: corner.tilt - refPoint.tilt,
      seat: (evenOddFlag && corner.seat % 2 === 0) ? corner.seat + 100 : corner.seat,
    }));

    if (evenOddFlag && !checkCornersSeatParity(updatedCorners)) {
      console.error(`Invalid corner seats, some are even and others are odd ${JSON.stringify(updatedCorners)}`);
      return false;
    }

    if (!updatedCorners.every((corner) => isCornerValid(corner))) {
      console.error('Invalid corner values after updating with respect to reference point');
      return false;
    }

    // if region does not exit, create a new region
    // Construct the new region and add it to the section's regions
    const newRegion = [updatedCorners[0], updatedCorners[1],
      updatedCorners[2], updatedCorners[3],
      {
        zoom_factor: zoomFactor,
        angle,
        corner_checked: cornerChecked,
        mapper_name: mapperName,
      }];
    section.regions.push(newRegion);

    console.log(`New region ${JSON.stringify(newRegion)} added to section ${sectionName}.`);

    await uploadMappingFile();
    const { mapping } = store.getState();
    const camIdx = mapping.connectedCamera;

    const result = await loadMapConfig(camIdx);
    return result;
  } catch (error) {
    console.error('Error adding region:', error);
    return false;
  }
};

/* note:
  Rove tab: always show raw position without considering ref point
  Browse tab: always show raw position without considering ref point
  preview pos: always show raw position without considering ref point
  Check tab ONLY: always show position with ref point calc
  position to sent to map file: mapFilePos = camPos - refPos

  Given that this function is only called from check tab, the values there are already
  calculated with respect to the reference point, so no need to subtract the reference point
*/
const updateRegion = async (
  selectedIndex,
  sectionName,
  sectionType,
  topLeft,
  topRight,
  bottomLeft,
  bottomRight,
  angle,
  zoomFactor,
  cornerChecked,
  mapperName,
) => {
  if (selectedIndex === undefined || sectionName === undefined || sectionType === undefined
    || ![topLeft, topRight, bottomLeft, bottomRight].every((corner) => isCornerValid(corner))
    || typeof angle !== 'number' || typeof zoomFactor !== 'number') {
    console.error('updateRegion: Invalid arguments');
    return null;
  }

  console.log(
    'updateRegion called:',
    'selectedIndex',
    selectedIndex,
    'selectedName',
    sectionName,
    'selectedType',
    sectionType,
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
    'angle',
    angle,
    'zoomFactor',
    zoomFactor,
    'cornerChecked',
    cornerChecked,
    'mapperName',
    mapperName,
  );

  try {
    let globalRegionIndex = 0;
    const newRegion = [topLeft, topRight, bottomLeft, bottomRight,
      {
        zoom_factor: zoomFactor,
        angle,
        corner_checked: cornerChecked,
        mapper_name: mapperName,
      }];

    for (let sectionIndex = 0; sectionIndex < camMap.sections.length; sectionIndex++) {
      const section = camMap.sections[sectionIndex];
      for (let regionIndex = 0; regionIndex < section.regions.length; regionIndex++) {
        if (globalRegionIndex === selectedIndex) {
          const nameChanged = section.name.trim().toLowerCase()
            !== sectionName.trim().toLowerCase();
          const typeChanged = section.type.trim().toLowerCase()
            !== sectionType.trim().toLowerCase();

          if (nameChanged || typeChanged) {
            const newName = sectionName.trim();
            const targetSection = camMap.sections.find((sec, idx) => idx !== sectionIndex
              && sec.name.trim().toLowerCase() === newName.toLowerCase());

            if (targetSection) {
              for (let i = 0; i < section.regions.length; i++) {
                const regionToAdd = (i === regionIndex) ? newRegion : section.regions[i];
                targetSection.regions.push(regionToAdd);
              }
              camMap.sections.splice(sectionIndex, 1);
            } else {
              section.name = newName;
              section.type = sectionType.trim().toLowerCase();
              section.regions[regionIndex] = newRegion;
            }
          } else {
            section.regions[regionIndex] = newRegion;
          }

          // eslint-disable-next-line no-await-in-loop
          await uploadMappingFile();
          const { mapping } = store.getState();
          const camIdx = mapping.connectedCamera;
          // eslint-disable-next-line no-await-in-loop
          const result = await loadMapConfig(camIdx);
          return result;
        }
        globalRegionIndex += 1;
      }
    }

    console.warn(`Region at index ${selectedIndex} not found`);
    return null;
  } catch (error) {
    console.error('Error updating region:', error);
    return null;
  }
};

const resetAllCornerChecked = async () => {
  try {
    if (!camMap?.sections) {
      console.error('resetAllCornerChecked: No mapping file loaded');
      return false;
    }

    let changed = false;
    camMap.sections.forEach((section) => {
      section.regions.forEach((region) => {
        const metadata = region[4];
        if (!metadata) return;
        if (metadata.corner_checked || metadata.mapper_name) {
          changed = true;
        }
        metadata.corner_checked = false;
        metadata.mapper_name = '';
      });
    });

    if (!changed) {
      console.log('resetAllCornerChecked: No regions had corner_checked set');
      return true;
    }

    await uploadMappingFile();
    const { mapping } = store.getState();
    const camIdx = mapping.connectedCamera;
    const result = await loadMapConfig(camIdx);
    return result;
  } catch (error) {
    console.error('Error resetting corner checked status:', error);
    return false;
  }
};

const parseSRSData = (separator) => {
  const dict = getRowDict();
  if (!camMap?.sections || !dict) {
    console.warn('Missing required data for SRS parsing');
    return [];
  }

  const rows = Object.keys(dict).sort((a, b) => dict[a] - dict[b]);
  const combinations = [];

  camMap.sections.forEach((section) => {
    section.regions.forEach((region) => {
      const topLeftCorner = region.find((corner) => corner.position === 'top_left');
      const bottomRightCorner = region.find((corner) => corner.position === 'bottom_right');

      if (!isCornerValid(topLeftCorner) || !isCornerValid(bottomRightCorner)) {
        console.warn(`Missing corners in section ${section.name}`);
        return;
      }

      const startRow = topLeftCorner.row;
      const endRow = bottomRightCorner.row;
      const startSeat = decodeOddEvenSeat(topLeftCorner.seat);
      const endSeat = decodeOddEvenSeat(bottomRightCorner.seat);

      const startRowIndex = rows.indexOf(startRow);
      const endRowIndex = rows.indexOf(endRow);

      if (startRowIndex === -1 || endRowIndex === -1) {
        console.warn(`Invalid row range: ${startRow} to ${endRow}`);
        return;
      }

      const minRowIndex = Math.min(startRowIndex, endRowIndex);
      const maxRowIndex = Math.max(startRowIndex, endRowIndex);
      const rowsInRange = rows.slice(minRowIndex, maxRowIndex + 1);
      const minSeat = Math.min(startSeat, endSeat);
      const maxSeat = Math.max(startSeat, endSeat);
      const seatStep = isOddEvenModeEnabled() ? 2 : 1;

      rowsInRange.forEach((row) => {
        for (let seat = minSeat; seat <= maxSeat; seat += seatStep) {
          combinations.push(`${section.name}${separator}${row}${separator}${seat}`);
        }
      });
    });
  });

  console.log('Generated SRS combinations:', combinations);
  return combinations;
};

const getPTZA = (section, row, seat) => {
  let regionObj;

  try {
    console.log('getPTZA called with:', { section, row, seat });

    const rowDict = getRowDict();
    if (!camMap || !rowDict) {
      console.log('Error: camMap or rowDict not loaded', { camMap: !!camMap, rowDict: !!rowDict });
      return null;
    }

    if (!section || !row || !seat || !Number.isInteger(parseInt(seat, 10))) {
      console.log('Error: Invalid SRS values', {
        section, row, seat, isInteger: Number.isInteger(parseInt(seat, 10)),
      });
      return null;
    }

    const sectionObj = camMap.sections.find(({ name }) => name === section);
    console.log('Found section:', sectionObj);

    if (!sectionObj) {
      console.log(`Error: Section ${section} not found in camMap`, { availableSections: camMap.sections.map((s) => s.name) });
      return null;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const region of sectionObj.regions) {
      if (isRowInRegion(row, region)) {
        const minSeat = Math.min(region[0].seat, region[1].seat);
        const maxSeat = Math.max(region[0].seat, region[1].seat);
        if (seat <= maxSeat && seat >= minSeat) {
          regionObj = region;
          console.log('Found matching region:', regionObj);
          break;
        }
      }
    }

    if (!regionObj) {
      console.log(`Error: could not find seat ${section}-${row}-${seat} in any region`);
      return null;
    }

    // Check for unmapped regions
    if (regionObj.some((corner) => corner.pan === null || corner.tilt === null)) {
      console.log('Warning: could not interpolate seat:', { section, row, seat }, 'in region:', regionObj);
      return null;
    }

    // Get reference point
    const refPoint = camMap.ref_point || { pan: 0, tilt: 0 };
    console.log('Using reference point:', refPoint);

    // Calculate pan and tilt using bilinear interpolation
    const { pan, tilt } = calculatePanTilt(row, seat, regionObj);

    const PTZA = {
      pan: pan + refPoint.pan,
      tilt: tilt + refPoint.tilt,
      zoom: regionObj[4].zoom_factor,
      angle: regionObj[4].angle,
    };

    console.log('Final PTZA calculation:', {
      input: { section, row, seat },
      output: PTZA,
      components: {
        region: regionObj,
        refPoint,
        rowOrder: getRowOrder(row),
      },
    });

    return PTZA;
  } catch (error) {
    console.log('An unexpected error occurred in getPTZA:', error);
    return null;
  }
};

const scanRegionToFile = (uiRegion) => {
  const fileRegion = {
    regionId: uiRegion.regionId,
    name: uiRegion.name,
    start: { pan: uiRegion.startPan, tilt: uiRegion.startTilt },
    end: { pan: uiRegion.endPan, tilt: uiRegion.endTilt },
    zoom: uiRegion.zoom,
  };
  if (uiRegion.reportUrl) {
    fileRegion.reportUrl = uiRegion.reportUrl;
  }
  if (Array.isArray(uiRegion.masks) && uiRegion.masks.length) {
    fileRegion.masks = uiRegion.masks.map((mask) => ({
      id: mask.id,
      points: mask.points.map(({ x, y }) => ({ x, y })),
    }));
  }
  if (uiRegion.arrangeLayout) {
    fileRegion.arrangeLayout = uiRegion.arrangeLayout;
  }
  ['rows', 'cols', 'tileWidth', 'tileHeight'].forEach((key) => {
    const n = Number(uiRegion[key]);
    if (Number.isFinite(n) && n > 0) fileRegion[key] = n;
  });
  return fileRegion;
};

/** Prefer tileWidth/tileHeight; else derive from legacy mosaic width/height ÷ cols/rows. */
const tileSizeFromFile = (fileRegion) => {
  const cols = Number(fileRegion?.cols);
  const rows = Number(fileRegion?.rows);
  let tileWidth = Number(fileRegion?.tileWidth);
  let tileHeight = Number(fileRegion?.tileHeight);
  if (!(tileWidth > 0) && cols > 0 && Number(fileRegion?.width) > 0) {
    tileWidth = Number(fileRegion.width) / cols;
  }
  if (!(tileHeight > 0) && rows > 0 && Number(fileRegion?.height) > 0) {
    tileHeight = Number(fileRegion.height) / rows;
  }
  return {
    tileWidth: tileWidth > 0 ? tileWidth : null,
    tileHeight: tileHeight > 0 ? tileHeight : null,
  };
};

const scanRegionFromFile = (fileRegion) => ({
  regionId: fileRegion?.regionId,
  name: fileRegion?.name || '',
  startPan: fileRegion?.start?.pan,
  startTilt: fileRegion?.start?.tilt,
  endPan: fileRegion?.end?.pan,
  endTilt: fileRegion?.end?.tilt,
  zoom: fileRegion?.zoom ?? fileRegion?.zoom_factor,
  reportUrl: fileRegion?.reportUrl ?? fileRegion?.report_url ?? null,
  rows: fileRegion?.rows ?? null,
  cols: fileRegion?.cols ?? null,
  ...tileSizeFromFile(fileRegion),
  tiles: Array.isArray(fileRegion?.tiles)
    ? fileRegion.tiles.map((tile) => ({
      url: tile.url,
      file: tile.file,
      row: tile.row,
      col: tile.col,
      pan: tile.pan,
      tilt: tile.tilt,
      zoom: tile.zoom,
    }))
    : [],
  masks: Array.isArray(fileRegion?.masks)
    ? fileRegion.masks
      .filter((mask) => mask?.id && Array.isArray(mask.points) && mask.points.length === 4)
      .map((mask) => ({
        id: String(mask.id),
        points: mask.points.map(({ x, y }) => ({ x: Number(x), y: Number(y) })),
      }))
    : [],
  arrangeLayout: fileRegion?.arrangeLayout || null,
});

const getScanRegionsForUi = () => {
  if (!camMap?.scan?.regions) return [];
  return camMap.scan.regions
    .map((region) => scanRegionFromFile(region))
    .filter((region) => region.regionId);
};

const saveScanRegions = async (uiRegions) => {
  if (!camMap) {
    console.error('saveScanRegions: camMap is invalid');
    return false;
  }

  try {
    camMap.scan = {
      make: camMap?.hardware?.device?.manufacturer || camMap?.scan?.make || '',
      model: camMap?.hardware?.device?.model || camMap?.scan?.model || '',
      regions: uiRegions.map(scanRegionToFile),
    };

    const uploadResult = await uploadMappingFile();
    if (!uploadResult) return false;

    const { mapping } = store.getState();
    return loadMapConfig(mapping.connectedCamera);
  } catch (error) {
    console.error('Error saving scan regions:', error);
    return false;
  }
};

const addScanRegion = async (uiRegion) => {
  const current = getScanRegionsForUi();
  return saveScanRegions([...current, uiRegion]);
};

const updateScanRegion = async (regionId, uiRegion) => {
  const current = getScanRegionsForUi();
  if (!current.some((region) => region.regionId === regionId)) {
    console.error('updateScanRegion: invalid regionId', regionId);
    return false;
  }
  return saveScanRegions(current.map((region) => (
    region.regionId === regionId ? { ...uiRegion, regionId } : region
  )));
};

/** Persist a scan-region update to a specific camera without swapping the in-memory map. */
const updateScanRegionForCamera = async (camIdx, regionId, uiRegion) => {
  const { mapping } = store.getState();
  if (camIdx && camIdx === mapping.connectedCamera) {
    return updateScanRegion(regionId, uiRegion);
  }

  try {
    const file = await fetchCamMapJson(camIdx);
    const current = (file?.scan?.regions || [])
      .map((region) => scanRegionFromFile(region))
      .filter((region) => region.regionId);
    if (!current.some((region) => region.regionId === regionId)) {
      console.error('updateScanRegionForCamera: invalid regionId', regionId);
      return false;
    }
    const nextFile = {
      ...file,
      scan: {
        make: file?.hardware?.device?.manufacturer || file?.scan?.make || '',
        model: file?.hardware?.device?.model || file?.scan?.model || '',
        regions: current.map((region) => (
          region.regionId === regionId ? { ...uiRegion, regionId } : region
        )).map(scanRegionToFile),
      },
    };
    return uploadCamMapJson(camIdx, nextFile);
  } catch (error) {
    console.error('Error updating scan region for camera:', error);
    return false;
  }
};

const deleteScanRegion = async (regionId) => {
  const current = getScanRegionsForUi();
  if (!current.some((region) => region.regionId === regionId)) {
    console.error('deleteScanRegion: invalid regionId', regionId);
    return false;
  }
  return saveScanRegions(current.filter((region) => region.regionId !== regionId));
};

export default {
  loadMapConfig,
  getReference,
  updateRefPoint,
  getRefImage,
  updateRefImage,
  populateCheckTabDropDownList,
  getRegion,
  deleteRegion,
  addRegion,
  updateRegion,
  resetAllCornerChecked,
  parseSRSData,
  getPTZA,
  getScanRegionsForUi,
  addScanRegion,
  updateScanRegion,
  updateScanRegionForCamera,
  deleteScanRegion,
  listMappingCams,
  fetchCamMapJson,
  getRowDictFile,
  uploadCamMapJson,
  // Internal functions for testing
  isValidJSONObject,
  isCornerValid,
  checkCornersSeatParity,
  getSection,
  getRegionByCorners,
  getRowOrder,
  isRowInRegion,
  interpolate2,
  calculatePanTilt,
  scanRegionToFile,
  scanRegionFromFile,
  _setCamMapForTesting,
  _getCamMapForTesting,
  setRowDict,
  _setRowDictForTesting,
};
